import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import {
  formatClientOptions,
  renderInputAffiliate,
} from "components/form/CommonInputs";
import { useClientContext } from "api/directory/client/ClientProvider";
import { useGeneralContext } from "api/directory/general/GeneralProvider";
import { FILTERS_FOR_NAMES } from "constants/constants";
import { DELIVERY_NOTE_FORM_LABELS } from "constants/forms";
import { formatCompanyAddress } from "utils/deliveryNoteHelper";
import PartyBlock from "./PartyBlock";

const RecipientBlock = ({
  control,
  register,
  errors,
  watch,
  setValue,
  isSubmitted,
  setCode,
  setStatus,
  onImageChange,
  resolveImage,
}) => {
  const { getNamesByFilters, namesByFilters } = useGeneralContext();
  const { getClientById, clientById } = useClientContext();
  const [pendingClientId, setPendingClientId] = useState(null);

  const clientOptions = useMemo(
    () =>
      formatClientOptions(
        (namesByFilters || []).filter(
          (e) => e.type === FILTERS_FOR_NAMES.CLIENT
        )
      ),
    [namesByFilters]
  );

  const setRecipientCompany = (name, selection) => {
    setValue(name, selection, { shouldValidate: isSubmitted });
    setValue("recipient_address", "");
    setValue("recipient_siret", "");
    setValue("recipient_phone", "");

    const id = selection?.[0]?.value;
    if (!id) return;
    setPendingClientId(id);
    getClientById({ id }).catch(() => setPendingClientId(null));
  };

  useEffect(() => {
    if (!pendingClientId || clientById?.id !== pendingClientId) return;
    setValue(
      "recipient_address",
      formatCompanyAddress({
        address: clientById.address,
        postalCode: clientById.postal_code,
        city: clientById.city,
      })
    );
    setPendingClientId(null);
  }, [clientById, pendingClientId, setValue]);

  return (
    <PartyBlock
      prefix="recipient"
      title={
        <FormattedMessage
          id="delivery_note_recipient_block"
          defaultMessage="Destinataire"
        />
      }
      logoValue={resolveImage(watch("recipient_logo_file_id"))}
      onLogoChange={(file) => onImageChange("recipient_logo_file_id", file)}
      companyField={renderInputAffiliate({
        control,
        register,
        name: "recipient_company",
        label: DELIVERY_NOTE_FORM_LABELS.company,
        value: watch("recipient_company"),
        setValue: setRecipientCompany,
        onDelete: () => setRecipientCompany("recipient_company", []),
        getOptions: (search) =>
          getNamesByFilters({ types: [FILTERS_FOR_NAMES.CLIENT], search }),
        clientsList: clientOptions,
        errors: errors.recipient_company,
        required: true,
      })}
      control={control}
      register={register}
      setCode={setCode}
      setStatus={setStatus}
    />
  );
};

RecipientBlock.propTypes = {
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  watch: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  isSubmitted: PropTypes.bool,
  setCode: PropTypes.func,
  setStatus: PropTypes.func,
  onImageChange: PropTypes.func.isRequired,
  resolveImage: PropTypes.func.isRequired,
};

export default RecipientBlock;
